/**
 * New / edit proposal — the screen that carries the app.
 *
 * One form, three parts, with the budget panel always visible: the event, who is coming, and
 * what that adds up to. The manager never has to guess the figure and then discover it was
 * wrong at approval time, which is the failure the email process has today.
 *
 * The draft is created on the server before the namelist can be edited, because the namelist
 * *is* a server resource (`PUT /proposals/:id/attendees` writes the snapshot HR will review).
 * So "Save draft" on a new proposal creates it and the URL changes; from then on every change
 * is a PATCH.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { useSession } from '@/hooks/session';
import { useLiveBudget } from '@/hooks/useCalculation';
import { BudgetPanel } from '@/components/BudgetPanel';
import { Namelist } from '@/components/Namelist';
import { ErrorNote, PageHeader } from '@/components/bits';
import { Alert, Button, Card, Dialog, Field, Input, Select, Textarea } from '@/components/ui';
import { ACTIVITY_TYPES, partitionIssues } from '@/lib/policy';
import { formatCentsPlain, parseAmountToCents } from '@/lib/money';
import { addDays, formatDateLong, sgtToday } from '@/lib/dates';

interface FormState {
  department_id: number | null;
  activity_type: string;
  activity_title: string;
  activity_description: string;
  scope_note: string;
  proposed_date: string;
  proposed_end_date: string;
  venue: string;
  remark: string;
}

const EMPTY: FormState = {
  department_id: null,
  activity_type: 'team_dinner',
  activity_title: '',
  activity_description: '',
  scope_note: '',
  proposed_date: '',
  proposed_end_date: '',
  venue: '',
  remark: '',
};

export function ProposalForm() {
  const params = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const session = useSession();
  const cache = useQueryClient();

  const proposalId = params.id ? Number(params.id) : null;

  const existing = useQuery({
    queryKey: ['proposal', proposalId],
    queryFn: () => api.proposal(proposalId as number),
    enabled: proposalId !== null,
  });

  const departments = useQuery({ queryKey: ['departments'], queryFn: api.departments });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [selected, setSelected] = useState<number[]>([]);
  const [requested, setRequested] = useState('');
  const [saveError, setSaveError] = useState<unknown>(null);
  const [askJustification, setAskJustification] = useState(false);
  const [justifications, setJustifications] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);

  /** Seed the form once from the server record; after that the user owns the fields. */
  useEffect(() => {
    if (loaded) return;
    if (proposalId === null) {
      const preferred = search.get('department');
      setForm({
        ...EMPTY,
        department_id: preferred ? Number(preferred) : (session.ownDepartmentIds[0] ?? null),
        // 21 days out: inside the 92-day horizon (D-47) and past the lead time, so the default
        // is a date that will not immediately be refused.
        proposed_date: addDays(sgtToday(), 21),
      });
      setLoaded(true);
      return;
    }
    const record = existing.data;
    if (!record) return;
    setForm({
      department_id: record.department.id,
      activity_type: record.activity_type,
      activity_title: record.activity_title,
      activity_description: record.activity_description ?? '',
      scope_note: record.scope_note ?? '',
      proposed_date: record.proposed_date,
      proposed_end_date: record.proposed_end_date ?? '',
      venue: record.venue ?? '',
      remark: record.remark ?? '',
    });
    setSelected(record.attendees.map((attendee) => attendee.employee_id));
    setRequested(
      record.requested_amount_cents === null ? '' : formatCentsPlain(record.requested_amount_cents),
    );
    setLoaded(true);
  }, [existing.data, loaded, proposalId, search, session.ownDepartmentIds]);

  const roster = useQuery({
    queryKey: ['roster', form.department_id],
    queryFn: () => api.employees({ department_id: form.department_id as number, limit: 500 }),
    enabled: form.department_id !== null,
  });

  const budget = useLiveBudget({
    departmentId: form.department_id,
    eventDate: form.proposed_date,
    employeeIds: selected,
    rosterIds: (roster.data?.employees ?? []).map((employee) => employee.id),
  });

  const requestedCents = requested.trim() === '' ? null : parseAmountToCents(requested);
  const requestedInvalid = requested.trim() !== '' && requestedCents === null;

  const editable =
    proposalId === null ||
    existing.data?.status === 'draft' ||
    existing.data?.status === 'returned';

  const options = useMemo(
    () =>
      (departments.data?.departments ?? []).filter(
        (department) => session.isHr || session.ownDepartmentIds.includes(department.id),
      ),
    [departments.data, session.isHr, session.ownDepartmentIds],
  );

  // ------------------------------------------------------------------------ mutations ----

  const save = useMutation({
    mutationFn: async (): Promise<number> => {
      const body = {
        activity_type: form.activity_type,
        activity_title: form.activity_title.trim(),
        activity_description: form.activity_description.trim() || null,
        scope_note: form.scope_note.trim() || null,
        proposed_date: form.proposed_date,
        proposed_end_date: form.proposed_end_date || null,
        venue: form.venue.trim() || null,
        remark: form.remark.trim() || null,
        requested_amount_cents: requestedCents,
      };

      const id =
        proposalId ??
        (await api.createProposal({ department_id: form.department_id as number, ...body })).id;
      if (proposalId !== null) await api.updateProposal(proposalId, body);
      if (selected.length > 0) await api.setProposalAttendees(id, selected);
      return id;
    },
    onSuccess: async (id) => {
      setSaveError(null);
      await cache.invalidateQueries({ queryKey: ['proposal', id] });
      await cache.invalidateQueries({ queryKey: ['proposals'] });
      if (proposalId === null) navigate(`/proposals/${id}/edit`, { replace: true });
    },
    onError: (error) => setSaveError(error),
  });

  const submit = useMutation({
    mutationFn: async () => {
      const id = await save.mutateAsync();
      return api.submitProposal(id, {
        ...(Object.keys(justifications).length > 0 ? { justifications } : {}),
        ...(requestedCents === null ? {} : { requested_amount_cents: requestedCents }),
      });
    },
    onSuccess: async (result) => {
      setSaveError(null);
      setAskJustification(false);
      // The record as well as the list: editing has already cached this proposal as a draft.
      await cache.invalidateQueries({ queryKey: ['proposal', result.id] });
      await cache.invalidateQueries({ queryKey: ['proposals'] });
      navigate(`/proposals/${result.id}`);
    },
    onError: (error) => {
      // The API is the authority on which warnings need a reason. If it says so, ask — the
      // form's own guess (`partitionIssues`) is only there to ask *first*.
      if (error instanceof ApiError && error.code === 'justification_required') {
        setAskJustification(true);
        return;
      }
      setSaveError(error);
    },
  });

  const needJustification = partitionIssues(budget.calculation?.issues ?? []).needJustification;
  const blocking = partitionIssues(budget.calculation?.issues ?? []).blocking;

  const readyToSubmit =
    form.department_id !== null &&
    form.activity_title.trim() !== '' &&
    form.proposed_date !== '' &&
    selected.length >= 2 &&
    !requestedInvalid &&
    blocking.length === 0;

  if (proposalId !== null && existing.isPending) {
    return <p className="text-muted-foreground text-sm">Loading the proposal…</p>;
  }

  return (
    <>
      <PageHeader
        title={
          proposalId === null ? 'Propose an event' : `Edit ${existing.data?.ref_no ?? 'draft'}`
        }
        subtitle="Say what the event is, pick who is coming, and the budget follows."
      />

      {!editable ? (
        <Alert tone="warn" title="This proposal can no longer be edited" className="mb-6">
          It has moved past the draft stage. Ask HR to return it if something needs changing.
        </Alert>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
        <div className="grid min-w-0 gap-6">
          <Card title="The event">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Department" required>
                {(props) => (
                  <Select
                    {...props}
                    value={form.department_id ?? ''}
                    disabled={!editable || proposalId !== null}
                    onChange={(event) => {
                      setForm({ ...form, department_id: Number(event.target.value) });
                      setSelected([]);
                    }}
                  >
                    <option value="">Choose a department</option>
                    {options.map((department) => (
                      <option key={department.id} value={department.id}>
                        {department.name} ({department.headcount})
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              <Field label="Activity type" required>
                {(props) => (
                  <Select
                    {...props}
                    value={form.activity_type}
                    disabled={!editable}
                    onChange={(event) => setForm({ ...form, activity_type: event.target.value })}
                  >
                    {ACTIVITY_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              <div className="sm:col-span-2">
                <Field label="Title" required hint="What you would call it in an email.">
                  {(props) => (
                    <Input
                      {...props}
                      value={form.activity_title}
                      disabled={!editable}
                      onChange={(event) => setForm({ ...form, activity_title: event.target.value })}
                    />
                  )}
                </Field>
              </div>

              <Field
                label="Date"
                required
                hint={
                  form.proposed_date
                    ? `${formatDateLong(form.proposed_date)} · up to three months ahead (D-47)`
                    : 'Up to three months ahead (D-47).'
                }
              >
                {(props) => (
                  <Input
                    {...props}
                    type="date"
                    value={form.proposed_date}
                    disabled={!editable}
                    onChange={(event) => setForm({ ...form, proposed_date: event.target.value })}
                  />
                )}
              </Field>

              <Field
                label="End date"
                hint={
                  form.proposed_end_date
                    ? formatDateLong(form.proposed_end_date)
                    : 'Only if it runs over more than one day.'
                }
              >
                {(props) => (
                  <Input
                    {...props}
                    type="date"
                    value={form.proposed_end_date}
                    disabled={!editable}
                    onChange={(event) =>
                      setForm({ ...form, proposed_end_date: event.target.value })
                    }
                  />
                )}
              </Field>

              <Field label="Venue">
                {(props) => (
                  <Input
                    {...props}
                    value={form.venue}
                    disabled={!editable}
                    onChange={(event) => setForm({ ...form, venue: event.target.value })}
                  />
                )}
              </Field>

              <Field
                label="Who is this for"
                hint="Name the group in words — “the two distribution crews”, “everyone in the Tuas yard”."
              >
                {(props) => (
                  <Input
                    {...props}
                    value={form.scope_note}
                    disabled={!editable}
                    onChange={(event) => setForm({ ...form, scope_note: event.target.value })}
                  />
                )}
              </Field>

              <div className="sm:col-span-2">
                <Field label="Remark" hint="Anything HR should know. Optional.">
                  {(props) => (
                    <Textarea
                      {...props}
                      value={form.remark}
                      disabled={!editable}
                      onChange={(event) => setForm({ ...form, remark: event.target.value })}
                    />
                  )}
                </Field>
              </div>
            </div>
          </Card>

          {form.department_id === null || form.proposed_date === '' ? (
            <Alert tone="neutral" title="Pick a department and a date">
              Service length — and therefore each person&rsquo;s entitlement — is measured against
              the event date, so the namelist needs both before it can show any figures.
            </Alert>
          ) : (
            <Namelist
              departmentId={form.department_id}
              eventDate={form.proposed_date}
              selected={selected}
              onChange={setSelected}
              figures={budget.figures}
              disabled={!editable}
            />
          )}
        </div>

        <div className="grid gap-4 lg:sticky lg:top-28">
          <BudgetPanel
            calculation={budget.calculation}
            pending={budget.pending}
            error={budget.error}
            requestedCents={requestedCents}
          >
            <Field
              label="Requesting"
              error={requestedInvalid ? 'Enter an amount like 1000 or 1000.50.' : null}
              hint={
                requestedInvalid
                  ? null
                  : 'Leave blank to request the whole available budget. GST-inclusive.'
              }
            >
              {(props) => (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-sm">S$</span>
                  <Input
                    {...props}
                    inputMode="decimal"
                    placeholder={
                      budget.calculation ? formatCentsPlain(budget.calculation.pool_cents) : '0.00'
                    }
                    value={requested}
                    disabled={!editable}
                    onChange={(event) => setRequested(event.target.value)}
                  />
                </div>
              )}
            </Field>
          </BudgetPanel>

          {saveError ? <ErrorNote error={saveError} /> : null}

          {selected.length === 1 ? (
            <Alert tone="warn" title="An event needs at least two people">
              One person is not a bonding activity.
            </Alert>
          ) : null}

          {editable ? (
            <div className="grid gap-2">
              <Button
                variant="primary"
                busy={submit.isPending}
                disabled={!readyToSubmit || save.isPending}
                onClick={() => {
                  if (needJustification.length > 0) {
                    setAskJustification(true);
                    return;
                  }
                  submit.mutate();
                }}
              >
                Submit for approval
              </Button>
              <Button busy={save.isPending} onClick={() => save.mutate()}>
                Save draft
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <JustificationDialog
        open={askJustification}
        issues={needJustification}
        values={justifications}
        onChange={setJustifications}
        busy={submit.isPending}
        onClose={() => setAskJustification(false)}
        onSubmit={() => submit.mutate()}
      />
    </>
  );
}

/**
 * The exception the manager is asking for, in their own words.
 *
 * Deliberately a dialog rather than an inline field: writing a justification is a separate act
 * from filling in a form, and the record shows both sides of it — the manager's reason and HR's
 * decision — so it should feel like signing something.
 */
function JustificationDialog({
  open,
  issues,
  values,
  onChange,
  busy,
  onClose,
  onSubmit,
}: {
  open: boolean;
  issues: Array<{ rule: string; message: string }>;
  values: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  busy: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const complete = issues.every((issue) => (values[issue.rule] ?? '').trim().length > 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="HR will need a reason"
      footer={
        <>
          <Button onClick={onClose}>Back</Button>
          <Button variant="primary" busy={busy} disabled={!complete} onClick={onSubmit}>
            Submit with these reasons
          </Button>
        </>
      }
    >
      <p className="text-muted-foreground">
        This is allowed — it just needs an explanation on the record. HR decides the exception
        before the budget can be confirmed.
      </p>
      {issues.map((issue) => (
        <Field key={issue.rule} label={`${issue.rule} — why`} required>
          {(props) => (
            <>
              <p className="text-muted-foreground mb-1.5 text-xs">{issue.message}</p>
              <Textarea
                {...props}
                value={values[issue.rule] ?? ''}
                onChange={(event) => onChange({ ...values, [issue.rule]: event.target.value })}
                placeholder="One or two sentences is enough."
              />
            </>
          )}
        </Field>
      ))}
    </Dialog>
  );
}
